import { createQueryParams } from '@/_utils/createQueryParams';
import { BagPaymentTermListApiService } from '@/bag/bag-payment-term-list/bag-payment-term-list-api.service';
import {
	BagPaymentOrganization,
	BagPaymentTermCurrency,
	BagPaymentTermFilters,
	BagPaymentTermListTableColumn,
	BagPaymentTermParameters,
	BagPaymentTermProduct,
	BagPaymentTermResponse,
	BagPaymentTermResponseResult,
	BagPaymentTermResponseResultSummaryItem,
	BagPaymentTermTableItem,
} from '@/bag/bag-payment-term-list/bag-payment-term-list.model';
import { ListComponent } from '@/common/component/list.component';
import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DataBagService } from 'src/app/common/data-bag.service';
import { Data2Service } from 'src/app/common/data2.service';
import { DateService } from 'src/app/common/date.service';
import { PAYMENT_STATUSES_MAP, PaymentStatuses, Status, statusMappings } from 'src/app/common/enums/Status';
import { InfoboxService } from 'src/app/common/infobox/infobox.service';
import { LoaderService } from 'src/app/common/loader/loader.service';
import { PricePipe } from 'src/app/common/pipes/price.pipe';
import { QuestionService } from 'src/app/common/question-dialog/question-dialog.service';

@Component({
	selector: 'app-bag-payment-term-list',
	templateUrl: './bag-payment-term-list.component.html',
	styleUrls: ['./bag-payment-term-list.component.scss'],
	providers: [PricePipe],
})
export class BagPaymentTermListComponent extends ListComponent {
	filters: BagPaymentTermFilters;
	parameters: BagPaymentTermParameters = {
		organizations: [],
		agencies: [],
		countKind: [
			{ id: 'V', name: 'Wartość' },
			{ id: 'P', name: 'Procent' },
			{ id: 'R', name: 'Pozostało do x%' },
		],
		status: [
			{ id: PaymentStatuses.PAID, name: PAYMENT_STATUSES_MAP[PaymentStatuses.PAID] },
			{ id: PaymentStatuses.NOT_PAID, name: PAYMENT_STATUSES_MAP[PaymentStatuses.NOT_PAID] },
			{ id: PaymentStatuses.PARTIALY_PAID, name: PAYMENT_STATUSES_MAP[PaymentStatuses.PARTIALY_PAID] },
			{ id: PaymentStatuses.PAID_TOO_MUCH, name: PAYMENT_STATUSES_MAP[PaymentStatuses.PAID_TOO_MUCH] },
		],
		reservationStatus: [
			{ id: Status.OPTION, name: statusMappings[Status.OPTION] },
			{ id: Status.CONFIRMED, name: statusMappings[Status.CONFIRMED] },
			{ id: Status.CANCELED, name: statusMappings[Status.CANCELED] },
			{ id: Status.REJECTED, name: statusMappings[Status.REJECTED] },
		],
		products: [],
		currency: [],
	};

	protected itemsBeforeDataLoad = 3;
	protected apiUrl = 'reservation/payment_term';
	protected apiType = 'bag';
	protected columns: BagPaymentTermListTableColumn[] = [
		{ id: 'term', name: 'Termin' },
		{ id: 'valueName', name: 'Kwota' },
		{ id: 'balanceValueName', name: 'Pozostało do zapłaty' },
		{ id: 'status', name: 'Status' },
		{ id: 'product', name: 'Produkt' },
		{ id: 'countKind', name: 'Sposób liczenia' },
		{ id: 'reservationOrganization', name: 'Organizator' },
		{ id: 'reservationSeller', name: 'Agent' },
		{ id: 'reservationStatus', name: 'Status rezerwacji' },
		{ id: 'reservationStayTime', name: 'Termin świadczenia' },
		{ id: 'tableId', name: 'Numer rezerwacji' },
	];
	protected summaryColumns: BagPaymentTermListTableColumn[] = [
		{ id: 'amount', name: 'Suma kwoty' },
		{ id: 'balance', name: 'Pozostało do zapłaty' },
	];

	constructor(
		protected api: Data2Service,
		protected bag: DataBagService,
		protected date: DateService,
		protected infobox: InfoboxService,
		protected route: ActivatedRoute,
		protected router: Router,
		protected question: QuestionService,
		protected loader: LoaderService,
		protected price: PricePipe,
		private readonly bagPaymentTermListApiService: BagPaymentTermListApiService,
	) {
		super(api, bag, date, infobox, route, router, question, loader);
	}

	convertData(data: BagPaymentTermResponse): BagPaymentTermResponseResult {
		data.results['summary'] = (this.generateSummary(data) as BagPaymentTermResponseResultSummaryItem[]).map(
			(obj) => {
				const currency = this.parameters.currency?.find((objFind) => objFind.id == obj.currency);
				obj.currency = currency ? currency.code : '?';
				obj.amount = (Number(obj.amount) * 0.01).toFixed(2) + ' ' + obj.currency;
				obj.balance = (Number(obj.balance) * 0.01).toFixed(2) + ' ' + obj.currency;
				return obj;
			},
		);
		data.results?.forEach((obj: BagPaymentTermTableItem) => {
			const currency = this.findObject(this.parameters.currency, obj.currency, 'code');
			obj.countKind = this.findObject(this.parameters.countKind, obj.countKind, 'name');
			obj.status = this.findObject(this.parameters.status, obj.status, 'name');
			obj.reservationSeller = this.findObject(this.parameters.agencies, obj.reservationSeller, 'shortName');
			obj.reservationOrganization = this.findObject(
				this.parameters.organizations,
				obj.reservationOrganization,
				'shortName',
			);
			obj.reservationStayTime =
				obj.reservationStayTimeLower && obj.reservationStayTimeUpper
					? `${obj.reservationStayTimeLower} - ${obj.reservationStayTimeUpper}`
					: '';
			obj.valueName = obj.value
				? `${this.price.transform(obj.value).toFixed(2).replace('.', ',')} ${currency}`
				: `0 ${currency}`;
			obj.balanceValueName = obj.balance
				? `${this.price.transform(obj.balance).toFixed(2).replace('.', ',')} ${currency}`
				: `0 ${currency}`;
			obj.reservationStatus = statusMappings[obj.reservationStatus];
			obj.tableId = {
				label: `BAG ${obj.tableId}`,
				isLink: true,
				route: `reservation/${obj.tableId}`,
			};
			obj.product = obj.offerProduct
				?.map((item: number) => {
					const product = this.parameters.products.filter((obj) => obj.id.toString() == item.toString());
					return product.length ? product[0].name : '';
				})
				.join(', ');
			return obj;
		});
		return data.results;
	}

	protected loadBefore(): void {
		this.getCurrencies();
		this.getOrganizations();
		this.getAgencies();
		this.getProducts();
	}

	protected generateSummary(data: BagPaymentTermResponse): BagPaymentTermResponseResultSummaryItem[] {
		return data.results.reduce((a: BagPaymentTermResponseResult, b: BagPaymentTermTableItem) => {
			const i = a.findIndex((x) => x.currency === b.currency);
			if (i === -1) {
				a.push({
					currency: b.currency,
					amount: b.value,
					balance: b.balance,
				});
			} else {
				(a[i].amount as number) += Number(b.value);
				(a[i].balance as number) += Number(b.balance);
			}
			return a;
		}, []) as BagPaymentTermResponseResultSummaryItem[];
	}

	protected findObject(arr: any[], item: string, l: string): string {
		return arr.find((v) => v.id == item)?.[l];
	}

	private async getCurrencies(): Promise<void> {
		const queryParams = {};
		const preparedQueryParams = createQueryParams(queryParams, `9999`);
		const currencies: BagPaymentTermCurrency[] =
			await this.bagPaymentTermListApiService.getCurrencies(preparedQueryParams);
		if (currencies) {
			currencies.forEach((obj: BagPaymentTermCurrency) => (obj.id = obj.id.toString()));
			this.parameters.currency = currencies;
		}
		this.loadData();
	}

	private async getOrganizations(): Promise<void> {
		const queryParams = {
			is_oskar: true,
		};
		const preparedQueryParams = createQueryParams(queryParams, `9999`);
		let organizations: BagPaymentOrganization[] =
			await this.bagPaymentTermListApiService.getOrganizations(preparedQueryParams);
		if (organizations) {
			organizations = organizations.map((organization: BagPaymentOrganization) => ({
				...organization,
				id: organization.id.toString(),
			}));
			this.parameters.organizations = organizations;
		}
		this.loadData();
	}

	private async getAgencies(): Promise<void> {
		const queryParams = {
			is_oskar: false,
		};
		const preparedQueryParams = createQueryParams(queryParams, `9999`);
		let agencies: BagPaymentOrganization[] =
			await this.bagPaymentTermListApiService.getAgencies(preparedQueryParams);
		if (agencies) {
			agencies = agencies.map((agency: BagPaymentOrganization) => ({
				...agency,
				id: agency.id.toString(),
			}));
			this.parameters.agencies = agencies;
		}
		this.loadData();
	}

	private async getProducts(): Promise<void> {
		const queryParams = {};
		const preparedQueryParams = createQueryParams(queryParams, `9999`);
		const products: BagPaymentTermProduct[] =
			await this.bagPaymentTermListApiService.getProducts(preparedQueryParams);
		if (products) {
			products.forEach((obj: BagPaymentTermProduct) => (obj.id = obj.id.toString()));
			this.parameters.products = products;
		}
		this.loadData();
	}
}
